import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Activity, DollarSign, Zap, TrendingUp, Clock, ChevronDown, AlertTriangle, ChevronRight } from 'lucide-react';
import { EmailDeliveryPanel } from './components/EmailDeliveryPanel';

const CALL_TYPE_LABELS = {
  gpt4o_vision_extraction: 'GPT-4o Invoice Extraction',
  document_ai_extraction: 'Document AI Extraction',
  slack_notification: 'Slack Notification',
  email_send: 'Email Sent',
  pdf_generation: 'PDF Generation',
};

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    loadSummary();
  }, [period]);

  useEffect(() => {
    loadHistory();
  }, [historyPage]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/system/usage/summary?days=${period}`);
      setSummary(data);
    } catch {
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/system/usage/history?skip=${historyPage * PAGE_SIZE}&limit=${PAGE_SIZE}`);
      setHistory(data.items);
      setHistoryTotal(data.total);
    } catch {
      // silent
    }
  };

  const formatCost = (c) => `$${(c || 0).toFixed(4)}`;
  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Settings
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>API usage tracking and system configuration</p>
        </div>
        {/* Period selector */}
        <div className="relative">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="appearance-none px-4 py-2.5 pr-9 rounded-lg text-sm font-medium cursor-pointer"
            style={{ background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            data-testid="period-selector"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
        </div>
      </div>

      {/* Configuration shortcuts */}
      <Link
        to="/ops/settings/repair-categories"
        className="block rounded-lg p-4 transition-all hover:scale-[1.01]"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none' }}
        data-testid="repair-categories-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SettingsIcon size={18} style={{ color: 'var(--primary)' }} />
            <div>
              <p className="font-bold text-sm font-['Manrope']" style={{ color: 'var(--text-primary)' }}>Repair Event Categories</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Manage the dropdown options shown in the New Repair modal</p>
            </div>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
        </div>
      </Link>

      {/* Email delivery diagnostics */}
      <EmailDeliveryPanel userEmail={user?.email} />

      {/* Master Admin — Danger Zone link */}
      {isMasterAdmin && (
        <Link
          to="/ops/data-reset"
          className="block rounded-lg p-4 transition-all hover:scale-[1.01]"
          style={{
            background: 'rgba(239, 68, 68, 0.06)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            textDecoration: 'none',
          }}
          data-testid="danger-zone-link"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle size={22} className="text-red-500 shrink-0" />
              <div>
                <p className="font-semibold text-red-400">Danger Zone — Data Reset</p>
                <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Permanently delete a customer's operational data or wipe all vendors. Soft-deleted for 30 days. Master Admin only.
                </p>
              </div>
            </div>
            <ChevronRight size={20} style={{ color: 'var(--text-muted)' }} />
          </div>
        </Link>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="usage-kpi-cards">
            <KPICard
              icon={Activity}
              label="Total API Calls"
              value={summary?.total_calls || 0}
              color="var(--primary)"
              testId="total-calls-card"
            />
            <KPICard
              icon={DollarSign}
              label="Estimated Cost"
              value={formatCost(summary?.total_cost)}
              color="var(--warning)"
              testId="total-cost-card"
            />
            <KPICard
              icon={Zap}
              label="Avg. Cost/Call"
              value={summary?.total_calls ? formatCost(summary.total_cost / summary.total_calls) : '$0.00'}
              color="var(--success)"
              testId="avg-cost-card"
            />
          </div>

          {/* Breakdown by Type */}
          <div className="rounded-lg p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="usage-breakdown">
            <h3 className="text-base font-semibold font-['Manrope'] mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <TrendingUp size={18} style={{ color: 'var(--primary)' }} />
              Usage Breakdown
            </h3>
            {summary?.breakdown?.length > 0 ? (
              <div className="space-y-3">
                {summary.breakdown.map((b) => {
                  const pct = summary.total_calls > 0 ? (b.count / summary.total_calls) * 100 : 0;
                  return (
                    <div key={b.call_type} data-testid={`breakdown-${b.call_type}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                          {CALL_TYPE_LABELS[b.call_type] || b.call_type}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{b.count} calls</span>
                          <span className="text-xs font-mono font-semibold" style={{ color: 'var(--warning)' }}>{formatCost(b.total_cost)}</span>
                        </div>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${Math.max(pct, 2)}%`, background: 'var(--primary)' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--text-faint)' }}>
                No API calls recorded in this period
              </p>
            )}
          </div>

          {/* Daily Trend */}
          {summary?.daily_trend?.length > 0 && (
            <div className="rounded-lg p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="daily-trend">
              <h3 className="text-base font-semibold font-['Manrope'] mb-4" style={{ color: 'var(--text-primary)' }}>
                Daily Activity
              </h3>
              <div className="flex items-end gap-1 h-24">
                {summary.daily_trend.map((d) => {
                  const maxCount = Math.max(...summary.daily_trend.map((x) => x.count));
                  const h = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
                  return (
                    <div
                      key={d.date}
                      className="flex-1 rounded-t transition-all duration-300 group relative"
                      style={{ height: `${Math.max(h, 4)}%`, background: 'var(--primary)', minWidth: 4, opacity: 0.8 }}
                      title={`${d.date}: ${d.count} calls, ${formatCost(d.cost)}`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] font-mono" style={{ color: 'var(--text-faint)' }}>
                  {summary.daily_trend[0]?.date}
                </span>
                <span className="text-[10px] font-mono" style={{ color: 'var(--text-faint)' }}>
                  {summary.daily_trend[summary.daily_trend.length - 1]?.date}
                </span>
              </div>
            </div>
          )}

          {/* Recent API Calls */}
          <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="usage-history">
            <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: 'var(--border)' }}>
              <h3 className="text-base font-semibold font-['Manrope'] flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Clock size={18} style={{ color: 'var(--primary)' }} />
                Recent API Calls
              </h3>
              <span className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>{historyTotal} total</span>
            </div>
            {history.length > 0 ? (
              <>
                <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {history.map((h) => (
                    <div key={h.id} className="px-5 py-3 flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                          {CALL_TYPE_LABELS[h.call_type] || h.call_type}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-faint)' }}>
                          {h.user_name || 'System'} &middot; {formatDate(h.created_at)}
                          {h.details?.items && ` \u00B7 ${h.details.items} items`}
                          {h.details?.method && ` \u00B7 ${h.details.method}`}
                        </p>
                      </div>
                      <span className="text-xs font-mono font-semibold" style={{ color: 'var(--warning)' }}>
                        {formatCost(h.estimated_cost)}
                      </span>
                    </div>
                  ))}
                </div>
                {historyTotal > PAGE_SIZE && (
                  <div className="px-5 py-3 flex items-center justify-between border-t" style={{ borderColor: 'var(--border)' }}>
                    <button
                      onClick={() => setHistoryPage(Math.max(0, historyPage - 1))}
                      disabled={historyPage === 0}
                      className="text-xs font-medium px-3 py-1.5 rounded disabled:opacity-30"
                      style={{ color: 'var(--primary)' }}
                      data-testid="history-prev-btn"
                    >
                      Previous
                    </button>
                    <span className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
                      Page {historyPage + 1} of {Math.ceil(historyTotal / PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => setHistoryPage(historyPage + 1)}
                      disabled={(historyPage + 1) * PAGE_SIZE >= historyTotal}
                      className="text-xs font-medium px-3 py-1.5 rounded disabled:opacity-30"
                      style={{ color: 'var(--primary)' }}
                      data-testid="history-next-btn"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--text-faint)' }}>
                No API calls recorded yet
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color, testId }) {
  return (
    <div
      className="rounded-lg p-5"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>{label}</span>
      </div>
      <p className="text-2xl font-bold font-['Manrope']" style={{ color: 'var(--text-primary)' }}>{value}</p>
    </div>
  );
}
